// src/shared/pagesToPdf.js — няколко снимки (JPEG) → ЕДИН PDF, страница по
// страница (ПРОМТ-37, „Качи от телефона").
//
// Защо на телефона, а не на сървъра: фактура на три листа е ЕДИН документ —
// един оригинал за OCR-а и за Конто. Сървърът приема един файл на документ
// (POST /api/purchases/upload) и пази PDF-а байт по байт, затова страниците
// се сглобяват преди качването.
//
// Без библиотека (правило на фазата): JPEG-ът влиза в PDF-а непроменен
// (/DCTDecode) — не се прекодира и не губи качество. Без дата и без случайни
// байтове: едни и същи снимки дават един и същ файл, а сървърът познава
// повторното качване по sha256 и не прави втори документ.

// A4 в точки и полето около снимката — като едностраничния PDF на сървъра
// (services/files.js: imageToPdf).
const A4 = [595.28, 841.89];
const MARGIN = 18;

// Размерът и цветовете от заглавката на JPEG-а (маркерите SOF0..SOF15 без
// DHT/JPG/DAC). Не е JPEG → грешка: такъв файл не бива да стига дотук.
export function jpegInfo(bytes) {
  const b = bytes;
  if (!(b[0] === 0xff && b[1] === 0xd8)) throw new Error('Снимката не е JPEG.');
  let i = 2;
  while (i + 9 < b.length) {
    if (b[i] !== 0xff) { i++; continue; }
    const m = b[i + 1];
    if (m === 0xd8 || m === 0x01 || (m >= 0xd0 && m <= 0xd7)) { i += 2; continue; }
    const len = (b[i + 2] << 8) | b[i + 3];
    if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) {
      return { height: (b[i + 5] << 8) | b[i + 6], width: (b[i + 7] << 8) | b[i + 8], components: b[i + 9] };
    }
    i += 2 + len;
  }
  throw new Error('Снимката не може да се прочете.');
}

const fmt = (n) => (Math.round(n * 100) / 100).toString();

// pages: [Uint8Array JPEG]. Връща Uint8Array с PDF-а.
export function pagesToPdf(pages) {
  if (!pages || !pages.length) throw new Error('Няма страници.');
  const enc = new TextEncoder();
  const parts = [];
  const offsets = [];
  let size = 0;
  const push = (x) => { const u = typeof x === 'string' ? enc.encode(x) : x; parts.push(u); size += u.length; };
  const obj = (n, body) => { offsets[n] = size; push(n + ' 0 obj\n'); body(); push('\nendobj\n'); };

  // 1 — каталог, 2 — страниците; за всяка страница по три обекта.
  const n = pages.length;
  const pageNo = (k) => 3 + k * 3;
  // Заглавка + ред с байтове над 127: по него програмите разбират, че
  // файлът е двоичен, и не го „поправят" при пренасяне.
  push('%PDF-1.4\n%âãÏÓ\n');
  obj(1, () => push('<< /Type /Catalog /Pages 2 0 R >>'));
  obj(2, () => push('<< /Type /Pages /Kids [' + pages.map((_, k) => pageNo(k) + ' 0 R').join(' ') + '] /Count ' + n + ' >>'));
  pages.forEach((jpeg, k) => {
    const info = jpegInfo(jpeg);
    const land = info.width > info.height;
    const [W, H] = land ? [A4[1], A4[0]] : A4;
    const scale = Math.min((W - 2 * MARGIN) / info.width, (H - 2 * MARGIN) / info.height);
    const w = info.width * scale, h = info.height * scale;
    const x = (W - w) / 2, y = (H - h) / 2;
    const p = pageNo(k), c = p + 1, im = p + 2;
    obj(p, () => push('<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + fmt(W) + ' ' + fmt(H) + '] /Resources << /XObject << /Im0 ' + im + ' 0 R >> >> /Contents ' + c + ' 0 R >>'));
    const draw = 'q ' + fmt(w) + ' 0 0 ' + fmt(h) + ' ' + fmt(x) + ' ' + fmt(y) + ' cm /Im0 Do Q';
    obj(c, () => { push('<< /Length ' + draw.length + ' >>\nstream\n'); push(draw); push('\nendstream'); });
    const cs = info.components === 1 ? '/DeviceGray' : info.components === 4 ? '/DeviceCMYK /Decode [1 0 1 0 1 0 1 0]' : '/DeviceRGB';
    obj(im, () => {
      push('<< /Type /XObject /Subtype /Image /Width ' + info.width + ' /Height ' + info.height + ' /ColorSpace ' + cs
        + ' /BitsPerComponent 8 /Filter /DCTDecode /Length ' + jpeg.length + ' >>\nstream\n');
      push(jpeg);
      push('\nendstream');
    });
  });
  const total = 3 + n * 3;
  const xref = size;
  let x = 'xref\n0 ' + total + '\n0000000000 65535 f \n';
  for (let k = 1; k < total; k++) x += String(offsets[k]).padStart(10, '0') + ' 00000 n \n';
  push(x + 'trailer\n<< /Size ' + total + ' /Root 1 0 R >>\nstartxref\n' + xref + '\n%%EOF\n');

  const out = new Uint8Array(size);
  let at = 0;
  for (const u of parts) { out.set(u, at); at += u.length; }
  return out;
}
